/**
 * Tipinio dienos suvartojimo modelio skaičiavimai
 */

export function calculateTypicalDayProfile(consumptionData) {
  if (!consumptionData || consumptionData.length === 0) return null;

  // 1. Sugrupuojame duomenis pagal paros laiką (15 min intervalai: 00:00, 00:15, ..., 23:45)
  // Reikia 96 laiko taškų
  const intervals = Array.from({ length: 96 }, (_, i) => {
    const hours = Math.floor(i / 4).toString().padStart(2, '0');
    const minutes = ((i % 4) * 15).toString().padStart(2, '0');
    return `${hours}:${minutes}`;
  });

  const groupedByTime = {};
  intervals.forEach(time => {
    groupedByTime[time] = [];
  });

  // Taip pat sugrupuojame pagal dienas individualioms linijoms
  const individualDays = {};

  consumptionData.forEach(record => {
    if (record.type !== 'P+') return; // Analizuojame tik pirkimą (vartojimą)

    const date = new Date(record.datetime);
    const hours = date.getHours().toString().padStart(2, '0');
    const minutes = date.getMinutes().toString().padStart(2, '0');
    const timeKey = `${hours}:${minutes}`;
    const dateKey = date.toISOString().split('T')[0];

    if (groupedByTime[timeKey]) {
      groupedByTime[timeKey].push(record.kwh);
    }

    if (!individualDays[dateKey]) {
      individualDays[dateKey] = Array(96).fill(null);
    }
    const intervalIdx = date.getHours() * 4 + Math.floor(date.getMinutes() / 15);
    individualDays[dateKey][intervalIdx] = record.kwh;
  });

  // 2. Skaičiuojame statistinius rodiklius kiekvienam intervalui
  const stats = intervals.map((time, idx) => {
    const values = groupedByTime[time].sort((a, b) => a - b);
    if (values.length === 0) return { time, p25: 0, p50: 0, p75: 0, p95: 0 };

    return {
      time,
      p25: getPercentile(values, 0.25),
      p50: getPercentile(values, 0.50),
      p75: getPercentile(values, 0.75),
      p95: getPercentile(values, 0.95)
    };
  });

  // 3. Gladinimas (Smoothing) - slenkantis vidurkis per 4 intervalus (1 valandą)
  const smoothedStats = smoothData(stats, 4);

  // 4. Agregavimas į valandinį formatą (SUMAVIMAS)
  const hourlyStats = [];
  for (let i = 0; i < smoothedStats.length; i += 4) {
    const hourStats = smoothedStats.slice(i, i + 4);
    const sum = (arr, key) => arr.reduce((acc, item) => acc + item[key], 0);
    
    hourlyStats.push({
      hour: `${Math.floor(i / 4).toString().padStart(2, '0')}:00`,
      p25: sum(hourStats, 'p25'),
      p50: sum(hourStats, 'p50'),
      p75: sum(hourStats, 'p75'),
      p95: sum(hourStats, 'p95')
    });
  }

  // 5. Individualių dienų gladinimas ir agregavimas (SUMAVIMAS)
  const smoothedDays = {};
  Object.keys(individualDays).forEach(date => {
    const rawData = individualDays[date].map(v => v === null ? 0 : v);
    const smoothed = smoothRawArray(rawData, 4);
    const hourly = [];
    for (let i = 0; i < smoothed.length; i += 4) {
      const slice = smoothed.slice(i, i + 4);
      // Sumuojame 4 intervalus, kad gautume valandinį kWh suvartojimą
      hourly.push(slice.reduce((a, b) => a + b, 0));
    }
    smoothedDays[date] = hourly;
  });

  return {
    hourlyStats,
    individualDays: Object.values(smoothedDays)
  };
}

function getPercentile(data, percentile) {
  if (data.length === 0) return 0;
  const index = (data.length - 1) * percentile;
  const lower = Math.floor(index);
  const upper = Math.ceil(index);
  const weight = index - lower;
  if (upper >= data.length) return data[lower];
  return data[lower] * (1 - weight) + data[upper] * weight;
}

function smoothData(data, windowSize) {
  return data.map((_, i) => {
    const start = Math.max(0, i - Math.floor(windowSize / 2));
    const end = Math.min(data.length, start + windowSize);
    const window = data.slice(start, end);
    
    return {
      time: data[i].time,
      p25: window.reduce((a, b) => a + b.p25, 0) / window.length,
      p50: window.reduce((a, b) => a + b.p50, 0) / window.length,
      p75: window.reduce((a, b) => a + b.p75, 0) / window.length,
      p95: window.reduce((a, b) => a + b.p95, 0) / window.length,
    };
  });
}

function smoothRawArray(data, windowSize) {
  return data.map((_, i) => {
    const start = Math.max(0, i - Math.floor(windowSize / 2));
    const end = Math.min(data.length, start + windowSize);
    const window = data.slice(start, end);
    return window.reduce((a, b) => a + b, 0) / window.length;
  });
}
